import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Editor from "../../components/Editing/Editor";
import { editActions } from "../../actions/editActions";
import { hasWriteAccess } from "../../utils/utils";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import * as _ from "lodash";

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const EditDataActCodeCommon: React.FC<{ toggleModal: () => void; target: string }> = ({
  toggleModal,
  target
}) => {
  const dispatch = useDispatch();
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const editedObject = useSelector((state: State.Root) => state.editView.editedObject);
  const storedObject = useSelector((state: State.Root) => state.editView.storedObject);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);
  
  // Determine configuration based on target
  const isEscalator = target === "Escalator";
  const isLift = target === "Lift";
  const dataActCodesKey = isEscalator ? "escalatorDataActCodes" : "dataActCodes";
  const apiType = isEscalator ? "escalatorDataActs" : "dataActs";
  
  const isNew = !storedObject;

  const isEmptyString = useCallback(
    (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0),
    []
  );

  // Calculate disable delete status - matching original pattern
  const disableDelete = isEscalator 
    ? pdApi.escalatorFaults
      ? pdApi.escalatorFaults.findIndex(fault => fault.dataActId === editedObject.id)
      : -1
    : isLift 
    ?pdApi.liftFaults && pdApi.apmFaults
      ? [...pdApi.liftFaults, ...pdApi.apmFaults].findIndex(fault => fault.dataActId === editedObject.id)
      : -1
    : -1;

  const isFieldValid = useCallback(
    (field: string): ErrorStatus => {
      switch (field) {
        case "name": {
          const { name } = editedObject;
          if (isEmptyString(name)) {
            return { errorStatus: false, errorMsg: "The name cannot be empty." };
          } else {
            // eslint-disable-next-line
            const dataActCodes = pdApi[dataActCodesKey as keyof typeof pdApi] as any[];
            const isDuplicate = dataActCodes
              ? dataActCodes.findIndex(
                  item => item.name.trim().toLowerCase() == name.trim().toLowerCase() && editedObject.id !== item.id
                )
              : -1;
            return {
              errorStatus: isDuplicate === -1,
              errorMsg: isDuplicate !== -1 ? "The name must be unique." : ""
            };
          }
        }
        case "code": {
          const { code } = editedObject;
          if (code === "" || code === undefined) {
            return { errorStatus: false, errorMsg: "The code cannot be empty." };
          } else if (isNew) {
            // eslint-disable-next-line
            const dataActCodes = pdApi[dataActCodesKey as keyof typeof pdApi] as any[];
            const isDuplicate = dataActCodes?.some(item => item.code == code);
            return {
              errorStatus: !isDuplicate,
              errorMsg: isDuplicate ? "The code must be unique." : ""
            };
          }
          return { errorStatus: true, errorMsg: "" };
        }
        default:
          return { errorStatus: true, errorMsg: "" };
      }
    },
    [editedObject, pdApi, dataActCodesKey, isNew]
  );

  const isAllFieldsValid = useCallback((): boolean => {
    const temp = _.keys(editedObject);
    for (const key of temp) {
      if (!isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  }, [editedObject, isFieldValid]);

  const checkCode = isFieldValid("code");
  const checkName = isFieldValid("name");

  const permissions = hasWriteAccess(activeUser, target as Api.GroupName);

  return (
    <Editor
      itemName="Data Act Code"
      editedObject={editedObject}
      originalObject={storedObject}
      cancel={() => dispatch(editActions.cancelEditing())}
      update={() => dispatch(editActions.update(apiType as Api.OptionsGrud))}
      create={() => dispatch(editActions.add(apiType as Api.OptionsGrud))}
      remove={{
        callback: toggleModal,
        enabledTooltip: "Data Act",
        disabled: disableDelete === -1 ? false : true,
        disabledTooltip: ": Item linked to child components"
      }}
      hasWritePermission={permissions}
      isValid={isAllFieldsValid()}
    >
      <NumberField
        id="code"
        label="Code"
        value={editedObject.code}
        isError={!checkCode.errorStatus}
        errorMessage={checkCode.errorMsg}
        readonly={!isNew || !permissions}
        onChange={code => dispatch(editActions.setEdited({ ...editedObject, code }))}
      />
      <TextField
        id="Name"
        label="Name"
        value={editedObject.name}
        isError={!checkName.errorStatus}
        errorMessage={checkName.errorMsg}
        readonly={!permissions}
        onChange={name => dispatch(editActions.setEdited({ ...editedObject, name : name.trim() }))}
      />
    </Editor>
  );
};

export default EditDataActCodeCommon;